/**
 * 华奋达 Fn 层展示：FUNC_V2 解码与虚拟键码映射
 * 与 @hfd-sdk/keyboard-sdk 中 Fn 槽位编码一致（pageType>=128 为 FUNC_V2）
 */
import type { KeyCodeInfo } from "./keyCodeMapper";

export type HfdFnKeySlot = {
  pageType: number;
  param1: number;
  param2: number;
  param3: number;
};

/** 无 xsKeycode 的 Fn 功能键用合成码展示（避免与物理 HID 冲突） */
export const HFD_FN_SYNTHETIC_BASE = 0x0e000000;

export type HfdFnV2Entry = {
  name: string;
  value: number;
  keyType?: 1 | 2;
};

/** 与 SDK `Qe` 表对齐的 FUNC_V2 功能列表 */
export const FUNC_V2_MAP_HFD: HfdFnV2Entry[] = [
  { name: "恢复出厂设置", value: 4096, keyType: 2 },
  { name: "切换至 Windows 系统", value: 4097, keyType: 2 },
  { name: "切换至 MAC 系统", value: 4098, keyType: 2 },
  { name: "切换至 Android 系统", value: 4099, keyType: 2 },
  { name: "蓝牙通道 1", value: 8192, keyType: 1 },
  { name: "蓝牙通道 2", value: 8193, keyType: 1 },
  { name: "蓝牙通道 3", value: 8194, keyType: 1 },
  { name: "蓝牙通道 4", value: 8195, keyType: 1 },
  { name: "蓝牙通道 5", value: 8196, keyType: 1 },
  { name: "蓝牙通道 1 进入配对", value: 8197, keyType: 2 },
  { name: "蓝牙通道 2 进入配对", value: 8198, keyType: 2 },
  { name: "蓝牙通道 3 进入配对", value: 8199, keyType: 2 },
  { name: "蓝牙通道 4 进入配对", value: 8200, keyType: 2 },
  { name: "蓝牙通道 5 进入配对", value: 8201, keyType: 2 },
  { name: "无线回连", value: 8202, keyType: 1 },
  { name: "无线配对", value: 8203, keyType: 2 },
  { name: "切回 USB 模式", value: 8204, keyType: 1 },
  { name: "灯光效果切换", value: 12288, keyType: 1 },
  { name: "灯光颜色切换", value: 12289, keyType: 1 },
  { name: "灯光亮度+", value: 12290, keyType: 1 },
  { name: "灯光亮度-", value: 12291, keyType: 1 },
  { name: "灯光速度+", value: 12292, keyType: 1 },
  { name: "灯光速度-", value: 12293, keyType: 1 },
  { name: "关闭灯光", value: 12294, keyType: 1 },
  { name: "打开灯光", value: 12295, keyType: 1 },
  { name: "侧灯效果切换", value: 12296, keyType: 1 },
  { name: "侧灯颜色切换", value: 12297, keyType: 1 },
  { name: "侧灯亮度+", value: 12298, keyType: 1 },
  { name: "侧灯亮度-", value: 12299, keyType: 1 },
  { name: "侧灯速度+", value: 12300, keyType: 1 },
  { name: "侧灯速度-", value: 12301, keyType: 1 },
  { name: "关闭侧灯", value: 12302, keyType: 1 },
  { name: "打开侧灯", value: 12303, keyType: 1 },
  { name: "主灯区方向切换", value: 12304, keyType: 1 },
  { name: "主灯开关切换", value: 12305, keyType: 1 },
  { name: "氛围灯模式切换", value: 12306, keyType: 1 },
  { name: "氛围灯颜色切换", value: 12307, keyType: 1 },
  { name: "氛围灯亮度+", value: 12308, keyType: 1 },
  { name: "氛围灯速度+", value: 12309, keyType: 1 },
  { name: "侧灯色块颜色切换", value: 12310, keyType: 1 },
  { name: "全灯开/关切换", value: 12311, keyType: 1 },
  { name: "切换上一个界面", value: 16384, keyType: 1 },
  { name: "切换下一个界面", value: 16385, keyType: 1 },
  { name: "确认键按下", value: 16386, keyType: 1 },
];

/** FUNC_V2 → 已有图标/ i18n 键码（星闪/贝盈共用区间） */
const FUNC_V2_ICON_KEYCODE: Record<number, number> = {
  4096: 61952,
  4097: 61953,
  4098: 61954,
  12288: 62208,
  12289: 62209,
  12290: 62210,
  12291: 62211,
  12292: 62212,
  12293: 62213,
  12294: 62215,
  12295: 62215,
};

const FUNC_V1_ICON_KEYCODE: Record<number, number> = {
  1: 61952,
  8: 61953,
  9: 61954,
  11: 62208,
  12: 62209,
  13: 62210,
  14: 62211,
  15: 62212,
  16: 62213,
  17: 62215,
  22: 61961, // 锁定 Win
};

const hfdFnNameBySynthetic = new Map<number, string>();

export function registerHfdFnDisplayName(
  funcValue: number,
  name: string
): void {
  if (!funcValue || !name) return;
  hfdFnNameBySynthetic.set(HFD_FN_SYNTHETIC_BASE | funcValue, name);
}

for (const item of FUNC_V2_MAP_HFD) {
  registerHfdFnDisplayName(item.value, item.name);
}

export function isHfdFnSyntheticKeyCode(keyCode: number): boolean {
  return (
    Number.isFinite(keyCode) &&
    keyCode >= HFD_FN_SYNTHETIC_BASE &&
    keyCode < HFD_FN_SYNTHETIC_BASE + 0x01000000
  );
}

export function resolveHfdFnSyntheticKeyInfo(
  keyCode: number
): KeyCodeInfo | null {
  if (!isHfdFnSyntheticKeyCode(keyCode)) return null;
  const name = hfdFnNameBySynthetic.get(keyCode);
  if (!name) return null;
  return {
    name,
    type: "控制",
    comm: "",
    hex16: "",
    typeof: "control",
  };
}

export function decodeHfdFnV2FuncValue(slot: HfdFnKeySlot): number {
  const pageType = Number(slot.pageType ?? 0);
  if (pageType < 128) return 0;
  const longValue = ((pageType & 127) << 8) | Number(slot.param1 ?? 0);
  const shortValue = (Number(slot.param2 ?? 0) << 8) | Number(slot.param3 ?? 0);
  return shortValue > 0 ? shortValue : longValue;
}

export function decodeHfdFnV1FuncValue(slot: HfdFnKeySlot): number {
  return (
    (Number(slot.param1 ?? 0) << 16) |
    (Number(slot.param2 ?? 0) << 8) |
    Number(slot.param3 ?? 0)
  );
}

function toSyntheticDisplayKeyCode(funcValue: number): number {
  return HFD_FN_SYNTHETIC_BASE | funcValue;
}

export function resolveHfdFnFuncDisplayKeyCode(
  funcValue: number,
  options?: { v2?: boolean }
): number {
  if (!funcValue) return 0;
  const iconMap = options?.v2 ? FUNC_V2_ICON_KEYCODE : FUNC_V1_ICON_KEYCODE;
  const iconCode = iconMap[funcValue];
  if (iconCode) return iconCode;
  return toSyntheticDisplayKeyCode(funcValue);
}

export function lookupHfdFnV2Entry(
  funcValue: number
): HfdFnV2Entry | undefined {
  return FUNC_V2_MAP_HFD.find((item) => item.value === funcValue);
}
